import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from '@/contexts/TenantContext';
import { MASTER_TENANT_ID } from '@/types/tenant';
import { toast } from 'sonner';

export interface RubricCriterion {
  criterion: string;
  weight: number;
  description: string;
}

export interface RubricKnowledgeSource {
  name: string;
  url: string;
  extracted_text?: string;
  word_count?: number;
}

export interface RubricTemplate {
  id: string;
  name: string;
  description: string | null;
  category: 'patient' | 'customer' | 'coach' | 'examiner';
  criteria: RubricCriterion[];
  knowledge_sources: RubricKnowledgeSource[];
  show_to_users: boolean;
  is_default: boolean;
  is_active: boolean;
  assign_to: 'all' | 'specific_companies' | 'specific_users';
  assigned_companies: string[];
  assigned_users: string[];
  company_id: string | null;
  tenant_id: string | null;
  is_system?: boolean | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export const useRubricTemplates = (category?: string) => {
  const { tenant } = useTenant();
  const currentTenantId = tenant?.tenant_id;
  const isMasterTenant = !currentTenantId || currentTenantId === MASTER_TENANT_ID;
  
  return useQuery({
    queryKey: ['rubric-templates', category, currentTenantId],
    queryFn: async () => {
      let query = supabase
        .from('rubric_templates')
        .select('*')
        .eq('is_active', true);
      
      // Tenant isolation - white-label tenants see system templates + their own
      if (!isMasterTenant && currentTenantId) {
        query = query.or(`tenant_id.eq.${MASTER_TENANT_ID},tenant_id.eq.${currentTenantId}`);
      }
        
      if (category) {
        query = query.eq('category', category);
      }
        
      const { data, error } = await query.order('name', { ascending: true });
      if (error) throw error;
      return (data || []).map(item => ({
        ...item,
        criteria: Array.isArray(item.criteria) ? item.criteria : [],
        knowledge_sources: Array.isArray((item as any).knowledge_sources) ? (item as any).knowledge_sources : []
      })) as unknown as RubricTemplate[];
    }
  });
};

export const useRubricTemplate = (templateId?: string) => {
  return useQuery({
    queryKey: ['rubric-template', templateId],
    queryFn: async () => {
      if (!templateId) return null;
      
      const { data, error } = await supabase
        .from('rubric_templates')
        .select('*')
        .eq('id', templateId)
        .single();
        
      if (error) throw error;
      return {
        ...data,
        criteria: Array.isArray(data.criteria) ? data.criteria : [],
        knowledge_sources: Array.isArray((data as any).knowledge_sources) ? (data as any).knowledge_sources : []
      } as unknown as RubricTemplate;
    },
    enabled: !!templateId
  });
};

export const useAllRubricTemplates = () => {
  const { tenant } = useTenant();
  const currentTenantId = tenant?.tenant_id;
  const isMasterTenant = !currentTenantId || currentTenantId === MASTER_TENANT_ID;
  
  return useQuery({
    queryKey: ['rubric-templates-all', currentTenantId],
    queryFn: async () => {
      let query = supabase
        .from('rubric_templates')
        .select('*');
      
      // Tenant isolation - white-label tenants see system templates + their own
      if (!isMasterTenant && currentTenantId) {
        query = query.or(`tenant_id.eq.${MASTER_TENANT_ID},tenant_id.eq.${currentTenantId}`);
      }
      
      const { data, error } = await query
        .order('category', { ascending: true })
        .order('name', { ascending: true });
        
      if (error) throw error;
      return (data || []).map(item => ({
        ...item,
        criteria: Array.isArray(item.criteria) ? item.criteria : [],
        knowledge_sources: Array.isArray((item as any).knowledge_sources) ? (item as any).knowledge_sources : []
      })) as unknown as RubricTemplate[];
    }
  });
};

export const useCreateRubricTemplate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (template: Omit<RubricTemplate, 'id' | 'created_at' | 'updated_at'>) => {
      const insertData = {
        name: template.name,
        description: template.description,
        category: template.category,
        criteria: JSON.parse(JSON.stringify(template.criteria)),
        knowledge_sources: JSON.parse(JSON.stringify(template.knowledge_sources || [])),
        show_to_users: template.show_to_users,
        is_default: template.is_default,
        is_active: template.is_active,
        assign_to: template.assign_to,
        assigned_companies: template.assigned_companies,
        assigned_users: template.assigned_users,
        company_id: template.company_id,
        tenant_id: template.tenant_id,
        is_system: template.is_system ?? false,
        created_by: template.created_by
      };
      
      const { data, error } = await supabase
        .from('rubric_templates')
        .insert(insertData)
        .select()
        .single();
        
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rubric-templates'] });
      queryClient.invalidateQueries({ queryKey: ['rubric-templates-all'] });
      toast.success('Rubric template created successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create rubric template');
    }
  });
};

export const useUpdateRubricTemplate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...template }: Partial<RubricTemplate> & { id: string }) => {
      const updateData: Record<string, any> = {};
      if (template.name !== undefined) updateData.name = template.name;
      if (template.description !== undefined) updateData.description = template.description;
      if (template.category !== undefined) updateData.category = template.category;
      if (template.criteria !== undefined) updateData.criteria = JSON.parse(JSON.stringify(template.criteria));
      if (template.knowledge_sources !== undefined) updateData.knowledge_sources = JSON.parse(JSON.stringify(template.knowledge_sources));
      if (template.show_to_users !== undefined) updateData.show_to_users = template.show_to_users;
      if (template.assign_to !== undefined) updateData.assign_to = template.assign_to;
      if (template.assigned_companies !== undefined) updateData.assigned_companies = template.assigned_companies;
      if (template.assigned_users !== undefined) updateData.assigned_users = template.assigned_users;
      if (template.company_id !== undefined) updateData.company_id = template.company_id;
      if (template.tenant_id !== undefined) updateData.tenant_id = template.tenant_id;
      if (template.is_system !== undefined) updateData.is_system = template.is_system;
      
      const { data, error } = await supabase
        .from('rubric_templates')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
        
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rubric-templates'] });
      queryClient.invalidateQueries({ queryKey: ['rubric-templates-all'] });
      toast.success('Rubric template updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update rubric template');
    }
  });
};

export const useDeleteRubricTemplate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('rubric_templates')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rubric-templates'] });
      queryClient.invalidateQueries({ queryKey: ['rubric-templates-all'] });
      toast.success('Rubric template deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete rubric template');
    }
  });
};
